import { faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RoomRule } from '@spike/room-model';
import clsx from 'clsx';
import DescriptionText from 'components/UI/V3/DescriptionText';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import { Tooltip } from 'components/UI/V3/Tooltip/Tooltip';
import { FunctionComponent, useState, useEffect } from 'react';
import styles from './RuleSize.module.scss';
import { v4 as uuid } from 'uuid';
import { PetTypeIds } from '@spike/model';
import SizeRuleItem from './RuleSizeItem';
import { useMediaQuery } from 'react-responsive';

interface RuleSizeProps {
    rule: RoomRule;
    roomPetTypeIds: Array<string>;
    usedRulesInOtherSizeRules?: Array<Pick<RuleSizeData, 'petTypeId' | 'sizeId'>>;
    onChange: (rule: RoomRule) => void;
    onDelete?: () => void;
    onError?: (error: boolean) => void;
    className?: string;
}

export interface RuleSizeData {
    uuid: string;
    petTypeId: PetTypeIds.DOGS | PetTypeIds.CATS | PetTypeIds.EXOTICS | null;
    sizeId: string | null;
    quantity: number;
}

export const RuleSize: FunctionComponent<RuleSizeProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:960px)' });

    const sizes: Array<RuleSizeData> = JSON.parse(props.rule.data);
    const [allSizesUsed, setAllSizesUsed] = useState(false);

    const calculateHasAnyError = (sizesArray: Array<RuleSizeData>): boolean => {
        return sizesArray.some((size, index) => {
            if (!size.petTypeId || !size.sizeId) {
                return false;
            }
            const isDuplicateInOtherRules = (props.usedRulesInOtherSizeRules ?? []).some(
                usedRule => usedRule.petTypeId === size.petTypeId && usedRule.sizeId === size.sizeId
            );
            if (isDuplicateInOtherRules) {
                return true;
            }
            const isDuplicate = sizesArray.some(
                (s, i) => i !== index && s.petTypeId === size.petTypeId && s.sizeId === size.sizeId
            );
            if (!isDuplicate) {
                return false;
            }
            const firstOccurrenceIndex = sizesArray.findIndex(
                s => s.petTypeId === size.petTypeId && s.sizeId === size.sizeId
            );
            return firstOccurrenceIndex !== index;
        });
    };

    const isDuplicatedSize = (sizesArray: Array<RuleSizeData>, sizeToValidate: RuleSizeData, ignoreUuid?: string) => {
        if (!sizeToValidate.petTypeId || !sizeToValidate.sizeId) {
            return false;
        }

        return sizesArray.some(
            size =>
                size.uuid !== ignoreUuid &&
                size.petTypeId === sizeToValidate.petTypeId &&
                size.sizeId === sizeToValidate.sizeId
        )
            ? true
            : (props.usedRulesInOtherSizeRules ?? []).some(
                  size => size.petTypeId === sizeToValidate.petTypeId && size.sizeId === sizeToValidate.sizeId
              );
    };

    useEffect(() => {
        if (props.onError) {
            const currentSizes: Array<RuleSizeData> = JSON.parse(props.rule.data);
            const hasAnyError = calculateHasAnyError(currentSizes);
            props.onError(hasAnyError);
        }
    }, [props.rule.data, props.onError]);

    const changeSizeHandler = (updatedSize: RuleSizeData) => {
        if (isDuplicatedSize(sizes, updatedSize, updatedSize.uuid)) {
            return;
        }

        props.onChange({
            ...props.rule,
            data: JSON.stringify(sizes.map(size => (size.uuid === updatedSize.uuid ? updatedSize : size)))
        });
    };

    const deleteSizeHandler = (deletedSizeUuid: string) => {
        props.onChange({
            ...props.rule,
            data: JSON.stringify(sizes.filter(size => size.uuid !== deletedSizeUuid))
        });
    };

    const addSizeHandler = () => {
        props.onChange({
            ...props.rule,
            data: JSON.stringify([...sizes, { uuid: uuid(), quantity: 1, petTypeId: null, sizeId: null }])
        });
    };

    return (
        <div className={clsx(styles.container, props.className)}>
            <div className={styles.row}>
                <Label>Rule – By Pet Size</Label>
                <FontAwesomeIcon
                    icon={faTrashAlt}
                    onClick={props.onDelete}
                    className={styles.deleteIcon}
                />
            </div>
            <DescriptionText
                className={styles.description}
                text="Allow any combination of pet sizes up to your chosen limits."
            />
            <div className={styles.sizesContainer}>
                {sizes.map((size, index) => {
                    const isDuplicateInOtherRules = !!(
                        size.petTypeId &&
                        size.sizeId &&
                        (props.usedRulesInOtherSizeRules ?? []).some(
                            usedRule => usedRule.petTypeId === size.petTypeId && usedRule.sizeId === size.sizeId
                        )
                    );
                    const isDuplicate = !!(
                        size.petTypeId &&
                        size.sizeId &&
                        sizes.some((s, i) => i !== index && s.petTypeId === size.petTypeId && s.sizeId === size.sizeId)
                    );
                    const firstOccurrenceIndex =
                        size.petTypeId && size.sizeId
                            ? sizes.findIndex(s => s.petTypeId === size.petTypeId && s.sizeId === size.sizeId)
                            : -1;
                    const isFirstOccurrence = firstOccurrenceIndex === index;
                    const hasError = !!isDuplicateInOtherRules || !!(isDuplicate && !isFirstOccurrence);

                    return (
                        <SizeRuleItem
                            key={`${size.uuid}`}
                            roomPetTypeIds={props.roomPetTypeIds}
                            ruleSize={size}
                            usedRules={[
                                ...sizes.filter(s => s.uuid !== size.uuid),
                                ...(props.usedRulesInOtherSizeRules ?? []).map((usedRule, usedIndex) => ({
                                    uuid: `external-${usedIndex}`,
                                    quantity: 1,
                                    petTypeId: usedRule.petTypeId,
                                    sizeId: usedRule.sizeId
                                }))
                            ]}
                            error={hasError}
                            onChange={changeSizeHandler}
                            onDelete={sizes.length > 0 ? () => deleteSizeHandler(size.uuid) : undefined}
                            onUsedAllSizes={setAllSizesUsed}
                        />
                    );
                })}
                {allSizesUsed ? (
                    <div className={styles.tooltipContainer}>
                        <Tooltip
                            content="All sizes have already been added to this rule."
                            placement={isMobile ? 'right' : 'top'}
                        >
                            <span className={clsx(styles.addLink, styles.disabled)}>+ Add Size</span>
                        </Tooltip>
                    </div>
                ) : (
                    <span
                        onClick={addSizeHandler}
                        className={styles.addLink}
                    >
                        + Add Size
                    </span>
                )}
            </div>
        </div>
    );
};

export default RuleSize;
